import React from "react";
import { Box, Container, Grid, Heading, Text, Link } from "@chakra-ui/react";
import { Blueprint } from "./Blueprint";
import { Kicker } from "./Kicker";
import { IndustryButton } from "./IndustryButton";
import { useIndustry } from "./tokens";
import { loginCopy, staffGets, LOGIN_ROUTE, SITE_URL } from "./homeContent";

/**
 * Unauthenticated Home — the artboard's "Staff login" view. The sign-in form
 * itself is NOT reproduced: the real one (passkeys, SMS, password) lives at
 * /login, so the panel is an explainer plus the one button.
 */
export const StaffLoginView: React.FC = () => {
  const t = useIndustry();

  return (
    <Box as="main" bg={t.ground} color={t.text}>
      {/* Hero: steel field left, sign-in panel right; stacks under 800px */}
      <Grid templateColumns="repeat(auto-fit, minmax(min(100%, 400px), 1fr))" minH={{ base: "auto", md: "calc(100vh - 72px)" }}>
        <Box
          bg={t.steel}
          color={t.inverse}
          px={{ base: 5, md: 12, lg: 16 }}
          py={{ base: 10, md: 16 }}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          gap={12}
        >
          <Kicker onSteel>{loginCopy.kicker}</Kicker>
          <Box>
            <Heading
              as="h1"
              fontWeight={600}
              fontSize="clamp(36px, 4vw, 56px)"
              lineHeight={1.04}
              letterSpacing="-0.01em"
              maxW="14ch"
              mb={4}
              color={t.inverse}
            >
              {loginCopy.heading}
            </Heading>
            <Text fontSize="15px" lineHeight="24px" maxW="46ch" color={t.inverse} opacity={0.72}>
              {loginCopy.lede}
            </Text>
          </Box>
          <Text fontSize="12px" color={t.inverse} opacity={0.55}>
            {loginCopy.note}
          </Text>
        </Box>

        <Box display="flex" alignItems="center" justifyContent="center" px={{ base: 5, md: 12 }} py={{ base: 10, md: 16 }}>
          <Blueprint w="100%" maxW="400px" p={{ base: 6, md: 8 }} display="flex" flexDirection="column" gap={4}>
            <Box>
              <Heading as="h2" fontSize="24px" lineHeight={1.1} fontWeight={600} mb={1}>
                {loginCopy.panelTitle}
              </Heading>
              <Text fontSize="13px" color={t.muted}>
                {loginCopy.panelHelp}
              </Text>
            </Box>
            <Text fontSize="15px" lineHeight="22px" color={t.muted}>
              {loginCopy.panelBody}
            </Text>
            <IndustryButton
              to={LOGIN_ROUTE}
              title="Go to the staff sign-in page (work email + password, passkey or SMS code)"
              minH="44px"
              w="100%"
            >
              {loginCopy.signInLabel}
            </IndustryButton>
            <Box display="flex" justifyContent="flex-end" fontSize="13px">
              <Link
                href={SITE_URL}
                title="Leave the back office and return to the public TalentLink+ site"
                color={t.accent700}
                _hover={{ color: t.steel, textDecoration: "none" }}
              >
                {loginCopy.backToSiteLabel}
              </Link>
            </Box>
          </Blueprint>
        </Box>
      </Grid>

      {/* What staff get — ruled list, no cards */}
      <Container maxW="1200px" px={{ base: 5, md: 10 }} py={{ base: 12, md: 16 }}>
        <Kicker mb={8}>What staff get</Kicker>
        <Box as="ol" listStyleType="none" m={0} p={0}>
          {staffGets.map((item, i) => (
            <Grid
              as="li"
              key={item.n}
              templateColumns={{ base: "40px minmax(0, 1fr)", md: "48px minmax(0, 1fr)" }}
              gap={3}
              py={5}
              borderTopWidth="1px"
              borderTopStyle="solid"
              borderTopColor={t.ruleLight}
              borderBottomWidth={i === staffGets.length - 1 ? "1px" : 0}
              borderBottomStyle="solid"
              borderBottomColor={t.ruleLight}
            >
              <Text fontSize="13px" lineHeight="24px" fontWeight={600} letterSpacing=".08em" color={t.accent700}>
                {item.n}
              </Text>
              <Box>
                <Heading as="h3" fontSize="21px" lineHeight="24px" fontWeight={600} mb={1}>
                  {item.title}
                </Heading>
                <Text fontSize="15px" lineHeight="22px" color={t.muted}>
                  {item.body}
                </Text>
              </Box>
            </Grid>
          ))}
        </Box>
      </Container>
    </Box>
  );
};
